import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BlockWrapper,
  defineBlock,
  smartProps,
  useEditor,
  useInspectorControl,
} from "@smartfire/js-block-engine";
import { EditableHeading } from "@smartfire/components";
import clsx from "clsx";
import he from "he";
import React from "react";
import { useTranslation } from "react-i18next";
import { Post, SubMenu } from "../components";
import { LastTile } from "./LastTile";
import style from "./style.module.scss";

const Inspector = React.lazy(() => import("./inspector.js"));

export const HomePost = ({
  posts = [],
  childrenCat = [],
  catSelected,
  mainStyle,
  mobileStyle,
  IdCategorySelected,
  nbTilesDesktop,
  nbTilesMobile,
  hasLastTile,
  urlCircleLogoWhite,
  ...props
}) => {
  useInspectorControl(Inspector);
  const { isEditing } = useEditor();
  // React i18next
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width:" + style.mobileBreakpoint + ")");
  const nbTiles = isDesktop ? nbTilesDesktop : nbTilesMobile;

  const isMobile = useMediaQuery(`(max-width: ${style.mobileBreakpoint})`);

  const bgColor = (side) => {
    switch (mainStyle) {
      case "light":
        if (side === "left") {
          return style.green1;
        } else {
          return style.green;
        }
      case "dark":
        if (side === "left") {
          return style.darkGreen0;
        } else {
          return style.darkGreen;
        }
      default:
        return style.lightBeige;
    }
  };

  const textColor = () => {
    if (mobileStyle === "compact") {
      switch (mainStyle) {
        case "light":
          return "#f5f4ef";
        case "dark":
          return "#f5f4ef";
        case "transparent":
          if (isMobile) {
            return "#165227";
          } else {
            return "#f5f4ef";
          }
        default:
          return "#165227";
      }
    }
  };

  return (
    <BlockWrapper.Div className={clsx(style.block, style[mainStyle])}>
      <div className={style.home}>
        <div className={style.header}>
          <EditableHeading
            placeholder={`${t("Title")}...`}
            smartProp="title"
            className={style.title}
          />

          <SubMenu
            arrowLeftBgColor={bgColor("left")}
            arrowRightBgColor={bgColor("right")}
          >
            {!isEditing &&
              childrenCat &&
              childrenCat.map((cat, index) => (
                <a href={cat.link} key={index} className={style.catLink}>
                  {he.decode(cat.name)}
                </a>
              ))}
          </SubMenu>

          {catSelected?.description && (
            <div
              className={style.description}
              dangerouslySetInnerHTML={{ __html: catSelected.description }}
            />
          )}
        </div>

        <div
          className={clsx(
            style.posts,
            mobileStyle === "compact" && style.compact
          )}
        >
          {!isEditing ? (
            posts &&
            posts.length > 0 &&
            posts
              .slice(0, hasLastTile ? nbTiles - 1 : nbTiles)
              .map((post, index) => (
                <Post
                  key={index}
                  post={post}
                  isZoom={true}
                  whiteMobileVerbose={mainStyle !== "transparent"}
                  verboseMobile={mobileStyle === "compact"}
                  titleColor={textColor()}
                />
              ))
          ) : (
            <p className={style.adminBo}>
              {t("List of articles will be here (see preview)")}
            </p>
          )}

          {hasLastTile && <LastTile {...{ catSelected, urlCircleLogoWhite }} />}
        </div>
      </div>
    </BlockWrapper.Div>
  );
};

export default defineBlock({
  Block: HomePost,
  setup: {
    title: "Home Posts",
  },
  smartProps: {
    mainStyle: smartProps.string.withDefault("light"),
    mobileStyle: smartProps.string.withDefault("large"),
    IdCategorySelected: smartProps.string.withDefault("1"),
    nbTilesDesktop: smartProps.number.withDefault(6),
    nbTilesMobile: smartProps.number.withDefault(6),
    hasLastTile: smartProps.bool.withDefault(false),
    title: smartProps.object.withDefault({
      content: "",
      headingLevel: 2,
    }),
    lastTileTitle: smartProps.string.withDefault(""),
    lastTileText: smartProps.string.withDefault(""),
    postsList: smartProps.array.withDefault([]),
  },
});
